import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styles from '../../styles/cr.module.scss';
import { IWithErrorHandlingProps, withErrorHandling } from '../withErrorHandling';
import { useParams } from 'react-router-dom';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { CrCommandBar } from '../cr/CrCommandBar';
import TaskStageContainer from './TaskStageContainer';
import { DataContext } from '../DataContext';
import { IApp03Epic, IApp03Sprint, IApp03Task, IApp03Workflow, IApp03WorkflowStage, IEntityDescription1, IProject } from '../../types';
import { TaskPriority, WorkflowType, getWorkflowTypeString } from '../../refData/WorkflowType';
import { OrbUserContext } from '../OrbUserContext';
import { TaskView } from './TaskView';
import { CommandButton, DefaultButton, IContextualMenuItem, IContextualMenuProps, ISelectableOption, Toggle } from '@fluentui/react';
import { CrLoadingOverlay } from '../cr/CrLoadingOverlay';
import { WFStageForm } from './WFStageForm';
import { TaskShortForm } from './TaskShortForm';
import { TaskFormPage } from './TaskFormPage';
import { CrDropdown } from '../cr/CrDropdown';
import { CrChoiceGroup } from '../cr/CrChoiceGroup';
import { App03Frameworks } from '../../types/App03Workflow';
import { App03SprintStatus } from '../../types/App03Sprint';
import useMobileDetector from '../useMobileDetector';
import TaskBoardContainer from './TaskBoardContainer';
import { ReorderStagePayload, ReorderTaskPayload } from './App03TaskBoard';

interface WorkflowTasksProps extends IWithErrorHandlingProps {
    apiConnected: boolean;
    lastPortfolioIdForChilds?: number | null;
    frameworkID: number;
    onEntityDescriptionChange: (desc: IEntityDescription1) => void;

    selectedWorkItemsFilter: string | null;
    onWorkItemsFilterChange: (value: string | null) => void;
    onBacklogStagesCountChange?: (count: number) => void;

}

interface ShowDataResult {
    showData: boolean;
    noDataMessage: string;
}

export enum TaskFormType {
    ShortForm = "ShortForm",
    FullForm = "FullForm",
}

// Define reusable keys (for comparisons)
export const SprintFilterCategoryKeys = {
    Backlog: 'Backlog',
    CurrentSprint: 'Current Sprint',
    PlannedSprint: 'Planned Sprint',
    HistoricSprint: 'Historic Sprint',
    Epic: 'Epic',
    Archived: 'Archived',
} as const;


const WorkflowTasks = (props: WorkflowTasksProps) => {

    const isMobile = useMobileDetector();
    const { dataServices } = useContext(DataContext);
    const { userContext } = useContext(OrbUserContext);
    const { workflowIdForTasks } = useParams<{ workflowIdForTasks: string }>();
    const history = useHistory();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isBoardView, setIsBoardView] = useState<boolean>(true);
    const [showMyItemsOnly, setShowMyItemsOnly] = useState(false);
    const [showBacklog, setShowBacklog] = useState(false);
    const [isViewOnly, setIsViewOnly] = useState<boolean>(false);

    //show/hide options in Display toggle
    //const [showViewToggle, setShowViewToggle] = useState<boolean>(false);
    //const [showBacklogToggle, setShowBacklogToggle] = useState<boolean>(false);

    const [stageCatFilter, setStageCatFilter] = useState('Current Stages');
    //const [currentSprintText, setCurrentSprintText] = useState('Current Sprint');
    const [sprintCatFilter, setSprintCatFilter] = useState<string>(SprintFilterCategoryKeys.CurrentSprint);
    const [justMyTasks, setJustMyTasks] = useState<boolean>(false);
    const [showTaskForm, setShowTaskForm] = useState<TaskFormType>(null);
    const [showTaskView, setShowTaskView] = useState(false);
    const [selectedTask, setSelectedTask] = useState<number>(null);
    const [showStageForm, setShowStageForm] = useState(false);
    const [editStage, setEditStage] = useState(false);
    const [selectedStageToEdit, setSelectedStageToEdit] = useState<number>(null);
    const [loadStageDataCount, setLoadStageDataCount] = useState(0);
    const [loadTasksListDataCounter, setLoadTasksListDataCounter] = useState(0);
    const [sprints, setSprints] = useState<IApp03Sprint[]>([]);
    const [workflowEpics, setWorkflowEpics] = useState<IApp03Epic[]>([]);
    const [epicIDFilter, setEpicIDFilter] = useState<number>(null);
    const [sprintIDFilter, setSprintIDFilter] = useState<number>(null);
    const [historicSprintIDFilter, setHistoricSprintIDFilter] = useState<number>(null);
    const [plannedSprintIDFilter, setPlannedSprintIDFilter] = useState<number>(null);
    const [workflow, setWorkflow] = useState<IApp03Workflow>();
    const [ppProject, setPPProject] = useState<IProject>();
    const [newTaskDefaultStageID, setNewTaskDefaultStageID] = useState<number>(null);
    const [taskSelectionPreservedOnEmptyClick, setTaskSelectionPreservedOnEmptyClick] = useState<boolean>(false);
    const [stagesDirty, setStagesDirty] = useState<boolean>(false);
    const [tasksDirty, setTasksDirty] = useState<boolean>(false);


    // State object that keeps track of which stage bars are open.
    // The key is a string combination of "sprintId-stageId", e.g. "5-12" or "none-3".
    // This allows unique open/close tracking per sprint-stage pair.
    const [openStages, setOpenStages] = useState<{ [key: string]: boolean }>({});

    // Builds a unique key from epicId, sprintId, stageId, and stageCatFilter.
    // Any null values are replaced with "none" to avoid "undefined" keys.
    const makeKey = (
        epicId: number | null,
        sprintId: number | null,
        stageId: number,
        stageCatFilter: string | null
    ) => `${epicId ?? 'none'}-${sprintId ?? 'none'}-${stageId}-${stageCatFilter ?? 'none'}`;



    // Toggles open/close state for a specific combination.
    const handleToggleStage = (
        epicId: number | null,
        sprintId: number | null,
        stageId: number,
        stageCatFilter: string | null
    ) => {
        const key = makeKey(epicId, sprintId, stageId, stageCatFilter);
        setOpenStages(prev => ({
            ...prev,
            [key]: !prev[key],
        }));
    };



    const getAppUrl = (): string => {
        if (props.frameworkID === App03Frameworks.Scrum) {
            return "/a10";
        }
        else {
            return "/a1";
        }
    }

    const commandBarItems: ICommandBarItemProps[] = [
        {
            key: 'Workflows',
            text: 'Back to Workstreams',
            iconProps: { iconName: 'Back' },
            onClick: () => {
                if (props.lastPortfolioIdForChilds != null) {
                    history.push(`${getAppUrl()}/workflows-for-portfolio/${props.lastPortfolioIdForChilds}`);
                }
                else {
                    history.push(`${getAppUrl()}/workflows`);
                }
            },
        },
        {
            key: 'NewTask',
            text: 'New Work Item',
            disabled: isViewOnly,
            iconProps: { iconName: 'Add' },
            onClick: () => {
                handleAddNewTask();
            },
        },
        (selectedTask !== null) && {
            key: 'EditTask',
            text: 'Edit Work Item',
            disabled: isViewOnly,
            iconProps: { iconName: 'Edit' },
            onClick: () => {
                setTaskSelectionPreservedOnEmptyClick(true);
                setShowTaskForm(TaskFormType.FullForm);
            },
        },
        (selectedTask !== null) && {
            key: 'View',
            text: 'View',
            iconProps: { iconName: 'View' },
            onClick: () => {
                setShowTaskView(true);
            },
        },
        // (selectedTask !== null && workflow?.WorkflowTypeID === WorkflowType.Linked) && {
        //     key: 'Updates',
        //     text: 'Updates',
        //     iconProps: { iconName: 'MiniExpand' },
        //     onClick: (ev, item) => {
        //         const task: IApp03Task = workflow?.App03Tasks?.find(t => t.ID === selectedTask);
        //         const dateString: any = task.UpdatePeriod;
        //         const updatePeriod: Date = new Date(dateString);
        //         if (!isNaN(updatePeriod.getTime())) {
        //             const formattedDate: string = updatePeriod.toISOString().split('T')[0]; //'YYYY-MM-DD'
        //             const projectID: number = task?.ProjectID;
        //             if (projectID > 0) {
        //                 history.push(`/a2/sign-off-for-project/${projectID}/${workflow.ID}/${formattedDate}`);
        //             }

        //         } else {
        //             console.log('Task update period is not a valid date string.');
        //         }

        //     },
        // },
        {
            key: 'NewStage',
            text: 'New Stage',
            disabled: isViewOnly,
            iconProps: { iconName: 'CircleAddition' },
            onClick: () => {
                setSelectedStageToEdit(null);
                setEditStage(false);
                setShowStageForm(true);
            },
        },
        {
            key: 'EditStage',
            text: 'Edit Stage',
            disabled: isViewOnly,
            iconProps: { iconName: 'FullWidthEdit' },
            onClick: () => {
                setSelectedStageToEdit(null); //make initially null
                setEditStage(true);
                setShowStageForm(true);
            },
        },

    ].filter(Boolean);

    const farCommandBarItems: ICommandBarItemProps[] = [];

    const toggleBoardView = () => {
        if (isBoardView) {
            if (stagesDirty) {
                setStagesDirty(false);
                console.log('stages are dirty, re-load data');
                setLoadStageDataCount(loadStageDataCount + 1);
            }
            if (tasksDirty) {
                setTasksDirty(false);
                console.log('tasks are dirty, re-load tasks');
                setLoadTasksListDataCounter(loadTasksListDataCounter + 1);
            }
        }
        setIsBoardView(!isBoardView);
    }

    const toggleMyItems = () => {
        //console.log('showMyItemsOnly', !showMyItemsOnly);
        setJustMyTasks(!justMyTasks);
        setLoadTasksListDataCounter(loadTasksListDataCounter + 1);
        setShowMyItemsOnly(!showMyItemsOnly);
    };

    const toggleBacklog = () => {
        //console.log('showBacklog', !showBacklog);
        setShowBacklog(!showBacklog);
    };

    const handleAddNewTask = (defaultStageID?: number) => {
        setNewTaskDefaultStageID(defaultStageID);
        setSelectedTask(null);
        setShowTaskForm(TaskFormType.FullForm);
    }

    const handleStageReorder = async (payload: ReorderStagePayload) => {
        try {
            const finalPayload = {
                ...payload,
                StageCategory: stageCatFilter   // append category here
            };

            await dataServices.app03WorkflowStageService.reorderStage(finalPayload);
            setStagesDirty(true); //flag to reload data when switch back to list view
        } catch (err) {
            console.error("Error reordering stage", err);
        }
    };

    const handleTaskReorder = async (payload: ReorderTaskPayload) => {
        try {
            await dataServices.app03TaskService.reorderTask(payload);
            setTasksDirty(true); // flag to reload data if needed
        } catch (err) {
            console.error("Error reordering task", err);
        }
    };

    const handleTaskPriorityChange = async (taskID: number, newPriorityID: number) => {
        try {
            await dataServices.app03TaskService.updateTaskPriority({
                TaskID: taskID,
                TaskPriorityID: newPriorityID
            });

            setTasksDirty(true); //flag to reload data
        } catch (err) {
            console.error("Error updating task priority", err);
        }
    };



    const showTaskViewToggle = () => {
        setShowTaskView(!showTaskView);
    }

    // useEffect(() => {
    //     if (isMobile) {
    //         setIsBoardView(false);
    //     }
    // }, []);

    useEffect(() => {
        if (props.selectedWorkItemsFilter) {
            //console.log("User selected filter:", props.selectedWorkItemsFilter);

            // Construct an option object to call your existing handler
            const option = {
                key: props.selectedWorkItemsFilter,
                text: props.selectedWorkItemsFilter // text can just be same as key
            };

            if (props.frameworkID === App03Frameworks.Scrum) {
                handleSprintCatFilterChange(null, option);
            }
            else if (props.frameworkID === App03Frameworks.Kanban) {
                handleStageChange(null, option);
            }


        }
    }, [props.selectedWorkItemsFilter]);

    useEffect(() => {
        if (showTaskForm === TaskFormType.FullForm) {
            const scrollableParent = document.querySelector('#TaskFormPageContainer');
            //console.log('Scrollable Parent:', scrollableParent);
            scrollableParent?.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [showTaskForm]);

    useEffect(() => {
        const fetchWorkflowEpics = async () => {
            try {
                const epics = await dataServices.app03EpicService.readAllForAddTaskDropdown(
                    Number(workflowIdForTasks)
                );
                setWorkflowEpics(epics);

                // Get first epic (if exists) and set its ID as the default filter value
                const firstEpicID = epics.length > 0 ? epics[0].ID : null;
                setEpicIDFilter(firstEpicID);

            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };


        if (props.frameworkID === App03Frameworks.Scrum) {
            const fetchSprints = async () => {
                if (Number(workflowIdForTasks) > 0) {
                    setIsLoading(true);
                    try {
                        const sprints: IApp03Sprint[] = await dataServices.app03SprintService.readAllSprints(Number(workflowIdForTasks));
                        //console.log('sprints', sprints);
                        setSprints(sprints);
                        setCurrentSprintFilter(sprints, true);

                        //set defaults for historic and planned
                        const lastCompletedSprintID = sprints
                            .filter(s => s.SprintStatus === App03SprintStatus.Completed)
                            .sort((a, b) => b.SprintNumber - a.SprintNumber)[0]?.ID ?? null;

                        const firstPlannedSprintID = sprints
                            .filter(s => s.SprintStatus === App03SprintStatus.Planned)
                            .sort((a, b) => a.SprintNumber - b.SprintNumber)[0]?.ID ?? null;

                        setHistoricSprintIDFilter(lastCompletedSprintID);
                        setPlannedSprintIDFilter(firstPlannedSprintID);


                    } catch (error) {
                        console.error('Error fetching data:', error);
                    }
                    finally {
                        setIsLoading(false);
                    }
                }
            };

            fetchSprints();
        }

        fetchWorkflowEpics();

    }, [workflowIdForTasks]);

    const setCurrentSprintFilter = (sprints: IApp03Sprint[], setText?: boolean) => {
        // Find the active sprint first
        const activeSprint = sprints.find(s => s.SprintStatus === App03SprintStatus.Active) ?? null;

        // if (activeSprint != null && setText === true) {
        //     setCurrentSprintText(`Current Sprint (Sprint ${activeSprint.SprintNumber} - ${activeSprint.Title})`);
        // }

        // Extract ID (or null if no active sprint)
        const activeSprintID = activeSprint?.ID ?? null;

        setSprintIDFilter(activeSprintID);

    };


    useEffect(() => {
        const fetchWorkflow = async () => {
            if (Number(workflowIdForTasks) > 0) {
                setIsLoading(true);
                try {
                    //const workflow: IApp03Workflow = await dataServices.app03WorkflowService.read(Number(workflowIdForTasks), false, false, ['App03Tasks($select=ID,Title,WorkflowStageID,TaskPriorityID,Archived,ProjectID,UpdatePeriod;$expand=App03TaskUsers($select=ID,UserID))', 'App03WorkflowStages($select=ID,Title,App03WorkflowID,DisplayOrder;$orderby=DisplayOrder)']);
                    const workflow: IApp03Workflow = await dataServices.app03WorkflowService.read(
                        Number(workflowIdForTasks),
                        false,
                        false,
                        [
                            `App03WorkflowUsers($filter=UserId eq ${userContext.UserId};$select=ViewOnly)`,
                            'App03WorkflowStages($select=ID,Title,SystemStage,App03WorkflowID,DisplayOrder;$orderby=DisplayOrder)',
                            'App03Portfolio($select=Title)'
                        ]
                    );
                    const viewOnly = workflow.App03WorkflowUsers?.[0]?.ViewOnly ?? false;
                    setIsViewOnly(viewOnly);
                    if (workflow?.WorkflowTypeID === WorkflowType.ProjectWS) {
                        //load project
                        const ppProject: IProject = await dataServices.projectService.read(Number(workflow.PPProjectID));
                        setPPProject(ppProject);
                    }

                    const workflowTasks: IApp03Task[] = await dataServices.app03TaskService.readAllForStageCounters(Number(workflowIdForTasks));
                    workflow.App03Tasks = workflowTasks;
                    setWorkflow(workflow);
                    const backlogStagesCount = workflow.App03WorkflowStages.filter(x => x.SystemStage === 'Backlog')?.length;
                    props.onBacklogStagesCountChange?.(backlogStagesCount);

                    const entityDesc: IEntityDescription1 = {
                        Description1ForList: "",
                        Description1ForForm: "",
                        DescriptionLine1: "Work Items for Work Stream",
                        DescriptionLine2: workflow?.Title,
                    };

                    props.onEntityDescriptionChange(entityDesc);

                } catch (error) {
                    console.error('Error fetching data:', error);
                }
                finally {
                    setIsLoading(false);
                }
            }
        };

        fetchWorkflow();
    }, [workflowIdForTasks, loadStageDataCount, loadTasksListDataCounter]);

    const onSaveStage = (): void => {
        setLoadStageDataCount(loadStageDataCount + 1);
    }

    const handleStageChange = (_: any, option?: { key: string; text: string }) => {
        if (option) setStageCatFilter(option.key);
    };

    const handleEpicFilterChange = (option: { key: number | string; text: string }) => {
        if (option) {
            const epictId = option.key !== undefined && option.key !== null
                ? Number(option.key)
                : null;

            setEpicIDFilter(epictId);
        }
    };

    const handleSprintFilterChange = (option: { key: number | string; text: string }, filterCat: string) => {
        if (option) {
            const sprintId = option.key !== undefined && option.key !== null
                ? Number(option.key)
                : null;
            if (filterCat === SprintFilterCategoryKeys.HistoricSprint) {
                setHistoricSprintIDFilter(sprintId);
            }
            else if (filterCat === SprintFilterCategoryKeys.PlannedSprint) {
                setPlannedSprintIDFilter(sprintId);
            }
            setSprintIDFilter(sprintId);
        }
    };


    const handleSprintCatFilterChange = (_: any, option?: { key: string; text: string }) => {
        if (option) {
            setSprintCatFilter(option.key);
            if (option.key === SprintFilterCategoryKeys.Backlog || option.key === SprintFilterCategoryKeys.Archived) {
                setStageCatFilter(option.key);
                setSprintIDFilter(null);
            }
            else if (option.key === SprintFilterCategoryKeys.Epic) {
                setStageCatFilter(option.key);
                setSprintIDFilter(null);
            }
            else {
                if (option.key === SprintFilterCategoryKeys.CurrentSprint) {
                    //load current sprint tasks
                    setCurrentSprintFilter(sprints);
                }
                else if (option.key === SprintFilterCategoryKeys.HistoricSprint) {
                    //load HistoricSprint tasks
                    setSprintIDFilter(historicSprintIDFilter);
                }

                else if (option.key === SprintFilterCategoryKeys.PlannedSprint) {
                    //load PlannedSprint tasks
                    setSprintIDFilter(plannedSprintIDFilter);
                }

                setStageCatFilter('Current Stages');
            }
        }
    };

    const handleEditStageSelection = (stageID: number): void => {
        setSelectedStageToEdit(stageID);
    }

    const handleOnTaskView = (taskID: number) => {
        setSelectedTask(taskID);
        showTaskViewToggle();
    };

    const handleChangeSelectedTask = (taskID: number, showTaskForm?: TaskFormType) => {
        //console.log('in handleChangeSelectedTask', taskID, showTaskForm);
        setSelectedTask(taskID);
        if (showTaskForm) {
            if (showTaskForm === TaskFormType.FullForm) {
                //user clicks on blue task title to open full form in edit mode
                setShowTaskForm(TaskFormType.FullForm);
                setTaskSelectionPreservedOnEmptyClick(true);
            }
            else {
                setShowTaskForm(TaskFormType.ShortForm);
            }
        }
        else {
            setShowTaskForm(null);
        }
    };

    const showStageFormToggle = () => {
        setShowStageForm(!showStageForm);
    }

    const taskFormSharedProps = {
        showForm: true,
        workflowID: workflow?.ID,
        newTaskStageID: newTaskDefaultStageID || workflow?.NewTaskDefaultStageID,
        entityId: selectedTask,
        frameworkID: props.frameworkID,
        sprintFilterCategory: sprintCatFilter,
        onSaved: () => {
            setTaskSelectionPreservedOnEmptyClick(false);
            setShowTaskForm(null);
            setLoadTasksListDataCounter(loadTasksListDataCounter + 1);
        },
        onCancelled: () => {
            setTaskSelectionPreservedOnEmptyClick(false);
            setShowTaskForm(null);
        },
        ...props,
    };


    const getHeading = (): string => {
        // Kanban — simply return workflow title
        if (props.frameworkID === App03Frameworks.Kanban) {
            return workflow?.Title ?? '';
        }

        // SCRUM logic
        if (props.frameworkID === App03Frameworks.Scrum) {

            const filter = props.selectedWorkItemsFilter;

            if (!filter) return '';

            // 1. Current Sprint → requires sprint number + title
            if (filter === SprintFilterCategoryKeys.CurrentSprint) {
                const activeSprint = sprints.find(s => s.SprintStatus === App03SprintStatus.Active) ?? null;

                if (activeSprint) {
                    return `Work Items : Current Sprint (${activeSprint.SprintNumber} - ${activeSprint.Title})`;
                }
                else {
                    return `Work Items : Current Sprint`; // fallback if no active sprint
                }
            }

            // 2. Other filters map directly to names
            if (filter === SprintFilterCategoryKeys.HistoricSprint)
                return `Work Items : Historic Sprints`;

            if (filter === SprintFilterCategoryKeys.PlannedSprint)
                return `Work Items : Planned Sprints`;

            if (filter === SprintFilterCategoryKeys.Backlog)
                return `Work Items : Backlogs`;

            if (filter === SprintFilterCategoryKeys.Archived)
                return `Work Items : Archives`;

            if (filter === SprintFilterCategoryKeys.Epic)
                return `Work Items : Epics`;

            // Fallback
            return `Work Items`;
        }

        return '';
    };


    const getSecondHeading = (): string => {
        let secondHeading: string = '';
        const portfolioName: string = workflow?.App03Portfolio?.Title ?? '';
        switch (workflow.WorkflowTypeID) {
            case WorkflowType.General:
                secondHeading = `General work stream in portfolio ${portfolioName}`;
                break;
            case WorkflowType.ProjectWS:
                secondHeading = `Project Portfolio work steam in project ${ppProject?.Title}, portfolio ${portfolioName}`;
                break;
            case WorkflowType.StudyPlanner:
                secondHeading = `Study Planner work stream in portfolio ${portfolioName}`;
                break;
        }

        if (props.frameworkID === App03Frameworks.Scrum) {
            const activeSprint = sprints.find(s => s.SprintStatus === App03SprintStatus.Active) ?? null;

            if (activeSprint != null) {
                // Ensure secondHeading ends with a single full stop before appending sprint info
                if (!secondHeading.trim().endsWith('.')) {
                    secondHeading = secondHeading.trim() + '.';
                }

                const currentSprintText: string = ` Current Sprint is #${activeSprint.SprintNumber} - ${activeSprint.Title}`;
                secondHeading += currentSprintText;
            }
        }


        return secondHeading;
    };

    const getSprintDropdownOptions = (sprints: IApp03Sprint[]): ISelectableOption[] => {
        const options = sprints.map(s => ({
            key: s.ID,
            text: `Sprint ${s.SprintNumber} - ${s.Title}`,
        }));

        return options;
    };

    const getEpicDropdownOptions = (): ISelectableOption[] => {
        const options = workflowEpics.map(s => ({
            key: s.ID,
            text: s.Title,
        }));

        return options;
    };


    const getEffectiveSprintID = (): number | null => {
        return props.frameworkID === App03Frameworks.Scrum &&
            stageCatFilter === "Current Stages" &&
            sprintIDFilter === null
            ? -1 // we don't want to include any tasks
            : sprintIDFilter;
    };

    const getEffectiveEpicID = (): number | null => {
        return stageCatFilter === "Epic"
            ? epicIDFilter === null
                ? -1
                : epicIDFilter
            : null;

    };

    const getShowDataResult = (): ShowDataResult => {
        // Condition 1: Epic but no epicIDFilter
        if (stageCatFilter === 'Epic' && (epicIDFilter === null || epicIDFilter === undefined)) {
            return { showData: false, noDataMessage: 'No Epic defined.' };
        }

        // Framework: Scrum
        if (props.frameworkID === App03Frameworks.Scrum && stageCatFilter === 'Current Stages') {

            // Planned Sprint
            if (props.selectedWorkItemsFilter === SprintFilterCategoryKeys.PlannedSprint &&
                (sprintIDFilter === null || sprintIDFilter === undefined)) {
                return { showData: false, noDataMessage: 'No Planned Sprint available.' };
            }

            // Historic Sprint
            if (props.selectedWorkItemsFilter === SprintFilterCategoryKeys.HistoricSprint &&
                (sprintIDFilter === null || sprintIDFilter === undefined)) {
                return { showData: false, noDataMessage: 'No Historic Sprint available.' };
            }
        }

        // Default: show
        return { showData: true, noDataMessage: '' };
    };

    const { showData, noDataMessage } = getShowDataResult();

    const showViewToggle = showData && stageCatFilter !== 'Archived';

    const isCurrentStages = showData && stageCatFilter === 'Current Stages';

    const showBacklogToggle =
        props.frameworkID === App03Frameworks.Kanban
            ? isCurrentStages
            : isCurrentStages &&
            (
                sprintCatFilter === SprintFilterCategoryKeys.CurrentSprint ||
                sprintCatFilter === SprintFilterCategoryKeys.PlannedSprint
            );


    const displayMenuProps = React.useMemo<IContextualMenuProps>(() => {
        const items: IContextualMenuItem[] = [];

        // ---- View (List / Board) ----
        if (showViewToggle) {
            items.push(
                {
                    key: 'viewHeader',
                    text: 'View',
                    itemType: 2, // Header
                },
                {
                    key: 'listView',
                    text: 'List View',
                    canCheck: true,
                    checked: !isBoardView,
                    onClick: toggleBoardView,
                    iconProps: { iconName: 'BulletedList' },
                },
                {
                    key: 'boardView',
                    text: 'Board View',
                    canCheck: true,
                    checked: isBoardView,
                    onClick: toggleBoardView,
                    iconProps: { iconName: 'GridViewSmall' },
                }
            );
        }

        // ---- Items (always shown) ----
        items.push(
            {
                key: 'itemsHeader',
                text: 'Items',
                itemType: 2,
            },
            {
                key: 'myItems',
                text: 'My Items',
                canCheck: true,
                checked: showMyItemsOnly,
                onClick: toggleMyItems,
                iconProps: { iconName: 'Contact' },
            },
            {
                key: 'allItems',
                text: 'All Items',
                canCheck: true,
                checked: !showMyItemsOnly,
                onClick: toggleMyItems,
                iconProps: { iconName: 'People' },
            }
        );

        // ---- Backlog ----
        if (showBacklogToggle) {
            items.push(
                {
                    key: 'backlogHeader',
                    text: 'Backlog',
                    itemType: 2,
                },
                {
                    key: 'showBacklog',
                    text: showBacklog ? 'Hide Backlog' : 'Show Backlog',
                    canCheck: true,
                    checked: showBacklog,
                    onClick: toggleBacklog,
                    iconProps: { iconName: 'BacklogBoard' },
                }
            );
        }

        return { items };
    }, [
        showViewToggle,
        showBacklogToggle,
        isBoardView,
        showMyItemsOnly,
        showBacklog,
        toggleBoardView,
        toggleMyItems,
        toggleBacklog,
    ]);

    const filteredStages = useMemo(() => {
        if (!workflow) return [];

        const includeBacklogInCurrentStages = showBacklogToggle && showBacklog;

        return (
            workflow.App03WorkflowStages?.filter(stage => {
                if (!stageCatFilter) return true;

                switch (stageCatFilter) {
                    case 'Backlog':
                        return stage.SystemStage === 'Backlog';

                    case 'Current Stages': {
                        const allowedStages = includeBacklogInCurrentStages
                            ? ['Backlog', 'Active', 'Completed', 'Cancelled']
                            : ['Active', 'Completed', 'Cancelled'];

                        return allowedStages.includes(stage.SystemStage);
                    }

                    case 'Epic':
                        return ['Backlog', 'Active', 'Completed', 'Cancelled'].includes(stage.SystemStage);

                    case 'Archived':
                        return stage.SystemStage === 'Archived';

                    default:
                        return true;
                }
            }) ?? []
        );
    }, [
        workflow,
        stageCatFilter,
        showBacklog,
        showBacklogToggle,
    ]);


    if (showData) {
        farCommandBarItems.push({
            key: 'viewToggle',
            text: 'Toggle view',
            onRender: () => (
                <div
                    className={styles.crCommandBarContainer}
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                >
                    {/*!isMobile &&*/ (
                        <CommandButton
                            text="Display"
                            //iconProps={{ iconName: 'View' }}
                            menuProps={displayMenuProps}
                        />

                    )}
                </div>
            ),
        });
    }

    let showBoard = false;
    if (isBoardView /*&& !isMobile*/ && showData) {
        // Show board if stage is not Archived
        if (stageCatFilter !== 'Archived') {
            showBoard = true;
        }
    }


    //if we want all stages close on filter change then uncomment following
    // useEffect(() => {
    //     setOpenStages({});
    // }, [stageCatFilter, workflowIdForTasks]);

    return (
        <div className={`${styles.cr} ${styles.updatesContainer}`} style={{ width: '100%' }}>
            {
                workflow &&
                showTaskForm &&
                showTaskForm === TaskFormType.FullForm &&
                <div id='TaskFormPageContainer'>
                    <TaskFormPage
                        workflowTitle={workflow.Title}
                        workflowTemplateID={workflow.App03TemplateID}
                        taskTimeOption={workflow.TaskTimeOption}
                        defaultPlanningStrategy={workflow.DefaultPlanningStrategy}
                        assignedToDefaultUserID={workflow.AssignedToDefaultUserID}
                        epicID={getEffectiveEpicID()}
                        {...taskFormSharedProps} />
                </div>
            }

            {
                <div style={{ display: (showTaskForm === TaskFormType.FullForm) ? 'none' : 'block' }}>
                    <CrLoadingOverlay isLoading={isLoading} opaque={true} />
                    {/* <CrCommandBar items={commandBarItems} farItems={farCommandBarItems} /> */}
                    {
                        showStageForm &&
                        <WFStageForm
                            workflowID={Number(workflowIdForTasks)}
                            workflowTypeID={workflow.WorkflowTypeID}
                            editRequest={editStage}
                            showForm={showStageForm}
                            onEditStageSelection={handleEditStageSelection}
                            entityId={selectedStageToEdit}
                            {...props}
                            onSaved={() => {
                                onSaveStage();
                                showStageFormToggle();
                            }}
                            onCancelled={showStageFormToggle}
                            {...props}
                        />
                    }
                    <div>

                        <div className='tasks-header'>
                            <div className='tasks-info'>
                                <div>
                                    <h2 className={styles.listTitle}>{getHeading()}</h2>
                                    {/* <div style={{ fontStyle: 'italic', paddingLeft: '10px', paddingBottom: '5px' }}>
                                        {workflow && getSecondHeading()}
                                    </div> */}
                                </div>
                            </div>

                            {/* <div className="tasks-toggle">
                                <Toggle
                                    onText="My Tasks"
                                    offText="My Tasks"
                                    checked={justMyTasks}
                                    onChange={() => {
                                        setJustMyTasks(!justMyTasks);
                                        setLoadTasksListDataCounter(loadTasksListDataCounter + 1);
                                    }}
                                />
                            </div> */}
                        </div>
                        <div style={{ marginBottom: '5px' }}>
                            <CrCommandBar className={`${styles.crCommandBarNoBorder}`} items={commandBarItems} farItems={farCommandBarItems} />
                        </div>

                        <>
                            {/* Epic dropdown - independent of framework filter */}
                            {(stageCatFilter === 'Epic' || sprintCatFilter === SprintFilterCategoryKeys.Epic) && showData && (
                                <div style={{ marginTop: '2px', marginBottom: '20px' }}>
                                    <CrDropdown
                                        options={getEpicDropdownOptions()}
                                        selectedKey={epicIDFilter}
                                        onChange={(ev, option) => handleEpicFilterChange(option)}
                                        style={{ minWidth: 120 }}
                                    />
                                </div>
                            )}

                            {/* Historic and Planned dropdowns - only for Scrum */}
                            {props.frameworkID === App03Frameworks.Scrum &&
                                (sprintCatFilter === SprintFilterCategoryKeys.HistoricSprint ||
                                    sprintCatFilter === SprintFilterCategoryKeys.PlannedSprint) && showData && (
                                    <div style={{ marginTop: '2px', marginBottom: '20px' }}>
                                        {sprintCatFilter === SprintFilterCategoryKeys.HistoricSprint && (
                                            <CrDropdown
                                                options={getSprintDropdownOptions(
                                                    sprints
                                                        .filter(x => x.SprintStatus === App03SprintStatus.Completed)
                                                        .reverse()
                                                )}
                                                selectedKey={historicSprintIDFilter}
                                                onChange={(ev, option) =>
                                                    handleSprintFilterChange(option, SprintFilterCategoryKeys.HistoricSprint)
                                                }
                                                style={{ minWidth: 120 }}
                                            />
                                        )}
                                        {sprintCatFilter === SprintFilterCategoryKeys.PlannedSprint && (
                                            <CrDropdown
                                                options={getSprintDropdownOptions(
                                                    sprints.filter(x => x.SprintStatus === App03SprintStatus.Planned)
                                                )}
                                                selectedKey={plannedSprintIDFilter}
                                                onChange={(ev, option) =>
                                                    handleSprintFilterChange(option, SprintFilterCategoryKeys.PlannedSprint)
                                                }
                                                style={{ minWidth: 120 }}
                                            />
                                        )}
                                    </div>
                                )}
                        </>

                        {workflow && filteredStages && (
                            showData ? (
                                showBoard ? (
                                    // BOARD VIEW
                                    <TaskBoardContainer
                                        isViewOnly={isViewOnly}
                                        stages={filteredStages}
                                        stageCatFilter={stageCatFilter}
                                        sprintCatFilter={sprintCatFilter}
                                        workflowID={workflow.ID}
                                        frameworkID={props.frameworkID}
                                        archived={null}
                                        sprintID={getEffectiveSprintID()}
                                        epicID={getEffectiveEpicID()}
                                        userIDFilter={justMyTasks ? userContext.UserId : undefined}
                                        onListsReorder={handleStageReorder}
                                        onTasksReorder={handleTaskReorder}
                                        onTaskEdit={(taskID) => handleChangeSelectedTask(taskID, TaskFormType.FullForm)}
                                        onTaskShortEdit={(taskID) => handleChangeSelectedTask(taskID, TaskFormType.ShortForm)}
                                        onTaskView={handleOnTaskView}
                                        onNewTaskClick={handleAddNewTask}
                                        onTaskPriorityChange={handleTaskPriorityChange}
                                    />
                                ) : (
                                    // LIST VIEW
                                    <>
                                        {filteredStages.map((stage, index) => (
                                            <TaskStageContainer
                                                key={stage.ID}
                                                isViewOnly={isViewOnly}
                                                workflowID={workflow.ID}
                                                stage={stage}
                                                sprintID={getEffectiveSprintID()}
                                                epicID={getEffectiveEpicID()}
                                                isOpen={
                                                    !!openStages[
                                                    `${epicIDFilter ?? 'none'}-${sprintIDFilter ?? 'none'}-${stage.ID}-${stageCatFilter ?? 'none'}`
                                                    ]
                                                }
                                                onToggleStage={() =>
                                                    handleToggleStage(epicIDFilter, sprintIDFilter, stage.ID, stageCatFilter)
                                                }

                                                counts={
                                                    workflow.App03Tasks?.filter(t => {
                                                        const sprintID = getEffectiveSprintID();
                                                        const epicID = getEffectiveEpicID();

                                                        const isBacklogStage = stage.SystemStage === "Backlog";

                                                        return (
                                                            t.WorkflowStageID === stage.ID &&
                                                            t.Archived !== true &&
                                                            (epicID === null || t.EpicID === epicID) &&
                                                            (
                                                                // ignore sprint filter for backlog stage
                                                                isBacklogStage ||
                                                                // otherwise apply existing sprint logic
                                                                props.frameworkID !== App03Frameworks.Scrum ||
                                                                sprintID === null ||
                                                                t.SprintID === sprintID
                                                            )
                                                        );
                                                    }).length ?? 0
                                                }

                                                countsUser={
                                                    workflow.App03Tasks?.filter(t => {
                                                        const sprintID = getEffectiveSprintID();
                                                        const epicID = getEffectiveEpicID();

                                                        const isBacklogStage = stage.SystemStage === "Backlog";

                                                        return (
                                                            t.WorkflowStageID === stage.ID &&
                                                            t.Archived !== true &&
                                                            (epicID === null || t.EpicID === epicID) &&
                                                            t.App03TaskUsers.some(user => user.UserID === userContext.UserId) &&
                                                            (
                                                                // ignore sprint filter for backlog stage
                                                                isBacklogStage ||
                                                                // otherwise apply sprint logic
                                                                props.frameworkID !== App03Frameworks.Scrum ||
                                                                sprintID === null ||
                                                                t.SprintID === sprintID
                                                            )
                                                        );
                                                    }).length ?? 0
                                                }

                                                totalCritical={
                                                    workflow.App03Tasks?.filter(t => {
                                                        const sprintID = getEffectiveSprintID();
                                                        const epicID = getEffectiveEpicID();

                                                        const isBacklogStage = stage.SystemStage === "Backlog";

                                                        return (
                                                            t.WorkflowStageID === stage.ID &&
                                                            t.TaskPriorityID === TaskPriority.Critical &&
                                                            t.Archived !== true &&
                                                            (epicID === null || t.EpicID === epicID) &&
                                                            (
                                                                // ignore sprint filter for backlog stage
                                                                isBacklogStage ||
                                                                // otherwise apply sprint logic
                                                                props.frameworkID !== App03Frameworks.Scrum ||
                                                                sprintID === null ||
                                                                t.SprintID === sprintID
                                                            )
                                                        );
                                                    }).length ?? 0
                                                }

                                                totalUserCritical={
                                                    workflow.App03Tasks?.filter(t => {
                                                        const sprintID = getEffectiveSprintID();
                                                        const epicID = getEffectiveEpicID();

                                                        const isBacklogStage = stage.SystemStage === "Backlog";

                                                        return (
                                                            t.WorkflowStageID === stage.ID &&
                                                            t.TaskPriorityID === TaskPriority.Critical &&
                                                            t.Archived !== true &&
                                                            t.App03TaskUsers.some(user => user.UserID === userContext.UserId) &&
                                                            (epicID === null || t.EpicID === epicID) &&
                                                            (
                                                                // ignore sprint filter for backlog stage
                                                                isBacklogStage ||
                                                                // otherwise apply sprint logic
                                                                props.frameworkID !== App03Frameworks.Scrum ||
                                                                sprintID === null ||
                                                                t.SprintID === sprintID
                                                            )
                                                        );
                                                    }).length ?? 0
                                                }

                                                archived={null}
                                                userIDFilter={justMyTasks === true ? userContext.UserId : null}
                                                onSaveData={onSaveStage}
                                                onChangeSelectedTask={handleChangeSelectedTask}
                                                loadTasksListDataCounter={loadTasksListDataCounter}
                                                clearSelection={selectedTask === null}
                                                onNewTaskClick={handleAddNewTask}
                                                taskSelectionPreservedOnEmptyClick={taskSelectionPreservedOnEmptyClick}
                                                frameworkID={props.frameworkID}
                                                stageCatFilter={stageCatFilter}
                                                {...props}
                                            />
                                        ))}
                                    </>
                                )
                            ) : (
                                // SHOW WHEN showData() === FALSE
                                <div style={{ paddingLeft: '15px', color: '#666' }}>
                                    {noDataMessage}
                                </div>
                            )
                        )}

                        {/* for archived */}
                        {workflow && stageCatFilter === 'Archived' &&
                            <TaskStageContainer
                                workflowID={workflow.ID}
                                isViewOnly={isViewOnly}
                                stage={null}
                                sprintID={getEffectiveSprintID()}
                                epicID={getEffectiveEpicID()}

                                //isOpen={!!openStages[`${epicIDFilter ?? 'none'}-${sprintIDFilter ?? 'none'}-${stage.ID}`]}
                                //onToggleStage={() => handleToggleStage(epicIDFilter, sprintIDFilter, stage.ID)}



                                isOpen={!!openStages[`${-1}-${-1}-${-1}-${stageCatFilter ?? 'none'}`]}
                                onToggleStage={() => handleToggleStage(-1, -1, -1, stageCatFilter)} //dummy id -1 for epic, -1 for sprint and -1 for stage archived
                                archived={true}
                                userIDFilter={justMyTasks === true ? userContext.UserId : null}
                                countsUser={workflow.App03Tasks?.filter(t => t.Archived === true).length ?? 0}
                                counts={workflow.App03Tasks?.filter(t => t.Archived === true && t.App03TaskUsers.some(user => user.UserID === userContext.UserId)).length ?? 0}
                                onSaveData={onSaveStage}
                                onChangeSelectedTask={handleChangeSelectedTask}
                                loadTasksListDataCounter={loadTasksListDataCounter}
                                clearSelection={selectedTask === null}
                                taskSelectionPreservedOnEmptyClick={taskSelectionPreservedOnEmptyClick}
                                frameworkID={props.frameworkID}
                                stageCatFilter={stageCatFilter}
                                {...props}
                            />
                        }

                        {
                            workflow && showTaskForm == TaskFormType.ShortForm &&
                            <TaskShortForm {...taskFormSharedProps} />
                        }

                        {
                            workflow && showTaskView &&
                            <TaskView
                                showForm={showTaskView}
                                entityId={selectedTask}
                                entityName='Task'
                                onClosed={showTaskViewToggle}
                                loadEntity={() => dataServices.app03TaskService.readForView(selectedTask)}
                            />
                        }

                    </div>
                </div>
            }

        </div>

    );
};

export default withErrorHandling(WorkflowTasks);